/**
 * @file index.tsx
 *
 * @fileoverview Renders the Facebook tracking pixel script in the page. It is based on GA4 component.
 */

import React, { useContext, useEffect, useRef } from 'react';
import { checkUserConsentForService } from '~/jobiqo/services/cmp';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { SERVICES } from '~/jobiqo/lib/cmp';
import Router from 'next/router';
import Script from 'next/script';
import { PathContextInterface } from '~/jobiqo/models/app';
import { PathContext } from '~/jobiqo/context/app';

declare global {
  var fbq: any;
}

const usePageReloadIfFacebookPixelConsentGotRemoved = (
  currentFacebookPixelConsent,
) => {
  // If the consent has changed from true to false we need to reload
  // the page otherwise FacebookPixel does not get excluded.
  const previousFacebookPixelConsent = useRef<boolean>(false);
  const userContext: UserContextInterface = useContext(UserContext);

  // Save current consent state to compare.
  useEffect(() => {
    previousFacebookPixelConsent.current = checkUserConsentForService(
      userContext?.consents,
      SERVICES.FacebookPixel,
    );
  });

  // Compare if consent has changed from true to false.
  if (previousFacebookPixelConsent.current && !currentFacebookPixelConsent) {
    Router.reload();
  }
};

export const FacebookPixel = () => {
  const userContext: UserContextInterface = useContext(UserContext);
  const pathContext: PathContextInterface = useContext(PathContext);
  const testing = process.env.testing;
  const staging = process.env.staging;
  //
  const isUserConsentNeededAndGiven = checkUserConsentForService(
    userContext?.consents,
    SERVICES.FacebookPixel,
  );

  function trackPageView() {
    // Track FB pageView event only for homepage.
    if (
      typeof window !== 'undefined' &&
      typeof fbq !== 'undefined' &&
      (pathContext.path == '/' || pathContext.path.startsWith('/?'))
    ) {
      fbq('track', 'PageView');
    }
  }

  useEffect(() => {
    if (isUserConsentNeededAndGiven && !testing && !staging) {
      // Add extra timeout to be sure script is loaded on initial page load.
      setTimeout(trackPageView, 1000);
    }
  }, [pathContext, isUserConsentNeededAndGiven]);

  usePageReloadIfFacebookPixelConsentGotRemoved(isUserConsentNeededAndGiven);

  return isUserConsentNeededAndGiven && !testing && !staging ? (
    <>
      <Script
        id="FacebookPixel-script"
        type="text/javascript"
        async={true}
        onLoad={() => {
          trackPageView();
        }}
      >
        {`
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window,document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '654401453124338');
        `}
      </Script>
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          src="https://www.facebook.com/tr?id=654401453124338&ev=PageView&noscript=1"
          alt=""
        />
      </noscript>
    </>
  ) : null;
};

export default FacebookPixel;
